import { z } from "zod";

/**
 * Opening Hours type for facilities (clinics, cabinets, businesses).
 *
 * Supports multiple time intervals per day (e.g. morning + afternoon with lunch break).
 * Stored as JSONB in the `opening_hours` column of the `facilities` table.
 *
 * Format:
 * {
 *   monday: [{ open: "08:00", close: "12:00" }, { open: "14:00", close: "18:00" }],
 *   tuesday: [{ open: "09:00", close: "17:00" }],
 *   saturday: null,  // closed
 *   sunday: null,
 *   _source: "google_places" | "manual",
 *   _lastEnriched: "2026-04-10T..."
 * }
 */

export const DAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export type DayKey = (typeof DAY_KEYS)[number];

export interface TimeInterval {
  open: string; // "HH:mm"
  close: string; // "HH:mm"
}

export interface OpeningHours {
  monday?: TimeInterval[] | null;
  tuesday?: TimeInterval[] | null;
  wednesday?: TimeInterval[] | null;
  thursday?: TimeInterval[] | null;
  friday?: TimeInterval[] | null;
  saturday?: TimeInterval[] | null;
  sunday?: TimeInterval[] | null;
  _source?: "google_places" | "manual";
  _lastEnriched?: string;
  _descriptions?: string[]; // Human-readable from Google Places
}

// --- Zod schemas ---

const TimeIntervalSchema = z.object({
  open: z.string().regex(/^\d{2}:\d{2}$/, "Format: HH:mm"),
  close: z.string().regex(/^\d{2}:\d{2}$/, "Format: HH:mm"),
});

const DayScheduleSchema = z.array(TimeIntervalSchema).nullable();

export const OpeningHoursSchema = z.object({
  monday: DayScheduleSchema.optional(),
  tuesday: DayScheduleSchema.optional(),
  wednesday: DayScheduleSchema.optional(),
  thursday: DayScheduleSchema.optional(),
  friday: DayScheduleSchema.optional(),
  saturday: DayScheduleSchema.optional(),
  sunday: DayScheduleSchema.optional(),
  _source: z.enum(["google_places", "manual"]).optional(),
  _lastEnriched: z.string().optional(),
  _descriptions: z.array(z.string()).optional(),
});

// --- Helpers ---

/** Get the intervals for a given day, or empty array if closed */
export function getDayIntervals(
  hours: OpeningHours | null | undefined,
  day: DayKey,
): TimeInterval[] {
  if (!hours) return [];
  const val = hours[day];
  if (!val || !Array.isArray(val)) return [];
  return val;
}

/** Check if a day is open */
export function isDayOpen(hours: OpeningHours | null | undefined, day: DayKey): boolean {
  return getDayIntervals(hours, day).length > 0;
}

/** Create a default weekday schedule (Mon-Fri 09:00-17:00) */
export function defaultOpeningHours(): OpeningHours {
  const weekday: TimeInterval[] = [{ open: "09:00", close: "17:00" }];
  return {
    monday: [...weekday],
    tuesday: [...weekday],
    wednesday: [...weekday],
    thursday: [...weekday],
    friday: [...weekday],
    saturday: null,
    sunday: null,
    _source: "manual",
  };
}

/**
 * Migrate legacy single-interval format to array format.
 * Old: { monday: { open: "08:00", close: "17:00" } }
 * New: { monday: [{ open: "08:00", close: "17:00" }] }
 */
export function normalizeOpeningHours(
  raw: Record<string, unknown> | null | undefined,
): OpeningHours | null {
  if (!raw) return null;

  const result: OpeningHours = {};

  for (const day of DAY_KEYS) {
    const val = raw[day];
    if (val === null || val === undefined) {
      result[day] = null;
    } else if (Array.isArray(val)) {
      // Already new format
      result[day] = val as TimeInterval[];
    } else if (typeof val === "object" && val !== null && "open" in val && "close" in val) {
      // Legacy single-interval format → wrap in array
      result[day] = [val as TimeInterval];
    }
  }

  if (raw._source) result._source = raw._source as OpeningHours["_source"];
  if (raw._lastEnriched) result._lastEnriched = raw._lastEnriched as string;
  if (raw._descriptions) result._descriptions = raw._descriptions as string[];

  return result;
}
